<script setup lang="ts">
import type { CardProps } from '/#/components/base/card/card'
import type { IconProps } from '/#/components/base/icon/icon'
import type { ComputedRef } from 'vue'

import isUndefined from 'lodash-es/isUndefined'

import { isEmpty } from '/@/utils/is'

const props = withDefaults(defineProps<CardProps>(), {
  radius: undefined,
  color: 'white',
  elevated: false,
  topColor: undefined,
  infoTip: undefined,
  title: undefined,
  theme: 'light',
})

/**
 * @name isChecked
 * @description Ref for checkbox state, used to determine if the checkbox is checked
 */
const isChecked = ref(false)

/**
 * @name appearance
 * @description Computed background color based on the color prop
 */
const appearance = computed(() => [
  props.color,
  props.noBorder && 'no-border',
  props.noPadding && '!p-0',
  props.noBackground && '!bg-transparent',
  props.hover && 'hover:bg-slate-50',
])
/**
 * @name iconProps
 * @description Computed icon properties for the card icon.
 * If cardIcon is a string, returns an icon with that name and size 'xl'
 */
const iconProps: ComputedRef<Undefinable<IconProps>> = computed(() => {
  if (typeof props.cardIcon === 'string')
    return { name: props.cardIcon, size: 'xl' } as IconProps

  return props.cardIcon as IconProps
})

/**
 * @name topColor
 * @description Computes the border top color based on the topColor prop, e.g., 'primary' -> '#ff5247'
 */
const topColor: ComputedRef<string | undefined> = computed(() => {
  return useCssVar(`--${props.topColor}`).value
})

// TODO: image tagleri ref e alınacak fakat buradaki yapı neden böyle anlamadım @necmettin
/**
 * @name handleAvatarError
 * @description Handles the image loading error event by replacing the source of the image with a default dummy avatar.
 */
function handleAvatarError(event: Event) {
  if (props.cardAvatar) {
    const imgElement = event.target as HTMLImageElement
    imgElement.src = new URL('/public/icons/dummy.svg', import.meta.url).href
  }
}

/**
 * @name handleImageError
 * @description Handles the image loading error event by replacing the source of the image with a default dummy image.
 */
function handleImageError(event: Event) {
  const imgElement = event.target as HTMLImageElement
  imgElement.src = new URL('/public/icons/dummy-image.svg', import.meta.url).href
}
</script>

<template>
  <div class="card" :class="theme">
    <div class="card-wrapper" :class="[appearance]">
      <CardTitle
        v-if="!contentTitle && title"
        :title="title"
        :info-tip="infoTip"
        :theme="theme"
        :subtitle="subtitle"
      >
        <template #title-action>
          <slot name="title-action" />
        </template>
      </CardTitle>
      <div
        class="card-body"
        :class="{ 'body-with-image': cardImgSrc || cardIcon || cardAvatar }"
      >
        <div v-if="cardImgSrc || cardIcon || cardAvatar" class="card-image-icon">
          <img
            v-if="cardImgSrc"
            class="card-image"
            :class="roundedCardImage ? 'rounded-card-image' : ''"
            :src="cardImgSrc"
            alt="Content Thumbnail"
            @error="handleImageError"
          />
          <span v-if="cardIcon && iconProps" class="card-icon">
            <Icon v-bind="iconProps" />
          </span>
          <Avatar
            v-if="cardAvatar"
            :image="cardAvatar || '/public/icons/dummy.svg'"
            size="md"
            @error="handleAvatarError"
          />
        </div>
        <div class="card-text h-full">
          <div v-if="contentTitle" class="card-text-content">
            <div v-if="!linkTitleProps" class="content-title">{{ title }}</div>
            <Link v-else v-bind="linkTitleProps" />
            <div class="content-subtitle">{{ subtitle }}</div>
          </div>
          <slot />
          <div v-if="tags" class="tags-wrapper">
            <!--
              TODO: The `Tag` component is used here instead of the `Tags` component.
              Using `Tags` causes unexpected layout issues in table structures on certain pages,
              even when the `Card` component is not present. Since the exact cause of these
              issues remains unclear, we opted to use `Tag` here as a temporary solution until
              the problem is fully resolved. @sena
            -->
            <Tag
              v-for="label in tags.items"
              :key="label as string"
              :label="label as string"
              label-field="name"
            />
          </div>
          <div v-if="subText" class="sub-text">{{ subText }}</div>
        </div>
        <Flex v-if="useCheckbox" align-items="center">
          <Radio v-model="isChecked" value name="checkbox" />
        </Flex>
      </div>
      <Flex
        v-if="!isEmpty(footer) && !isUndefined(footer)"
        class="card-footer"
        :class="footer.variant"
        align-items="center"
        justify-content="space-between"
      >
        <slot name="footer-right-side" />
        <Icon
          v-if="!isEmpty(footer.iconName) && !isUndefined(footer.iconName)"
          :name="footer.iconName"
          size="xxl"
        />
        <div v-if="$slots.action">
          <slot name="action" />
        </div>
      </Flex>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-wrapper {
  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    border-radius: var(--radius) var(--radius) 0 0;
    height: 0.3rem;
    top: 0;
    left: 0;
    background-color: v-bind(topColor);
  }
}
</style>
